<template>
  <div class="matrix-renderer-wrap">
    <div class="screen" role="main">

      <div class="header">
        <button
          type="button"
          class="back-button"
          disabled
          :aria-label="chrome.back"
        >←</button>
        <span class="section-counter">{{ chrome.sectionCounter }}</span>
      </div>

      <div
        class="progress-bar"
        :aria-label="chrome.progress"
        aria-hidden="true"
      >
        <div class="progress-segment">
          <div class="progress-segment-fill" :style="{ width: '20%' }"></div>
        </div>
        <div class="progress-segment"></div>
        <div class="progress-segment"></div>
      </div>

      <div class="section-header">
        <p v-if="displaySubtitle" class="section-eyebrow">{{ displaySubtitle }}</p>
        <h1 class="section-title" :class="{ 'is-placeholder': isTitlePlaceholder }">
          {{ displayTitle }}
        </h1>
        <p v-if="displayPrompt" class="section-prompt">{{ displayPrompt }}</p>
        <p class="section-progress-text">
          {{ answeredCount }} {{ chrome.of }} {{ items.length }} {{ chrome.answered }}
        </p>
      </div>

      <div class="question-list">
        <div
          v-for="(item, idx) in displayItems"
          :key="item.id"
          class="question-row"
        >
          <div class="question-row-header">
            <p
              class="question-row-title"
              :class="{ 'is-placeholder': item.isLabelPlaceholder }"
            >{{ item.displayLabel }}</p>
            <button
              v-if="item.hasTooltip"
              type="button"
              class="tooltip-button-small"
              :aria-expanded="String(!!tooltipsOpen[item.id])"
              :aria-controls="`mtt-${uid}-${item.id}`"
              @click="toggleTooltip(item.id)"
            >ⓘ</button>
          </div>
          <div
            v-if="item.hasTooltip && tooltipsOpen[item.id]"
            :id="`mtt-${uid}-${item.id}`"
            class="tooltip-content"
          >{{ item.tooltipText }}</div>

          <div
            class="answer-chips"
            role="radiogroup"
            :aria-label="item.displayLabel"
          >
            <label
              v-for="opt in displayOptions"
              :key="opt.id"
              class="chip"
              :class="{
                'is-selected': selections[item.id] === opt.id,
                'is-placeholder': opt.isPlaceholder,
              }"
            >
              <input
                type="radio"
                :name="`mq-${uid}-${item.id}`"
                :value="opt.id"
                v-model="selections[item.id]"
              >
              <span class="chip-label">{{ opt.displayShort }}</span>
            </label>
          </div>
        </div>
      </div>

      <div class="footer">
        <button
          type="button"
          class="next-button"
          disabled
        >{{ chrome.next }}</button>
      </div>
    </div>
  </div>
</template>

<script>
// MatrixRenderer
//
// Production-style preview component for matrix questions, modelled on
// the Vanilla B Sectioned Scroll layout. Self-contained: takes one
// matrix question's content + active locale and renders the full
// section chrome (counter, progress, header, item rows, footer).
//
// The chrome strings ("Abschnitt 1 von 3", "Weiter", etc.) are
// illustrative — the editor preview doesn't know which questionnaire
// the question belongs to. They are localized so switching locale tabs
// switches the whole frame, not just the form fields.
//
// Scores are author-only and never displayed. The footer button is
// disabled in this preview context.

let rendererInstance = 0;

const CHROME = {
  en: {
    back: 'Back',
    progress: 'Progress',
    sectionCounter: 'Section 1 of 3',
    of: 'of',
    answered: 'answered',
    next: 'Next',
    placeholderTitle: 'Question title appears here',
    placeholderItem: 'Item label',
    placeholderShort: '—',
  },
  de: {
    back: 'Zurück',
    progress: 'Fortschritt',
    sectionCounter: 'Abschnitt 1 von 3',
    of: 'von',
    answered: 'beantwortet',
    next: 'Weiter',
    placeholderTitle: 'Frage-Titel erscheint hier',
    placeholderItem: 'Eintrag',
    placeholderShort: '—',
  },
  fr: {
    back: 'Retour',
    progress: 'Progression',
    sectionCounter: 'Section 1 sur 3',
    of: 'sur',
    answered: 'répondues',
    next: 'Suivant',
    placeholderTitle: 'Le titre de la question apparaît ici',
    placeholderItem: 'Élément',
    placeholderShort: '—',
  },
  it: {
    back: 'Indietro',
    progress: 'Avanzamento',
    sectionCounter: 'Sezione 1 di 3',
    of: 'di',
    answered: 'risposte',
    next: 'Avanti',
    placeholderTitle: 'Il titolo della domanda appare qui',
    placeholderItem: 'Elemento',
    placeholderShort: '—',
  },
};

export default {
  name: 'MatrixRenderer',
  props: {
    title: { type: Object, default: () => ({}) },
    subtitle: { type: Object, default: () => ({}) },
    prompt: { type: Object, default: () => ({}) },
    // Array of { id, label: i18n, label_short: i18n, score } per
    // SPEC_MATRIX_SCHEMA.md. Scores are ignored in the renderer.
    scaleOptions: { type: Array, default: () => [] },
    // Array of { id, label: i18n, tooltip_body: i18n }.
    items: { type: Array, default: () => [] },
    locale: { type: String, default: 'de' },
    // Show placeholder copy where required fields are still empty.
    preview: { type: Boolean, default: true },
  },
  data() {
    rendererInstance += 1;
    return {
      uid: `m-${rendererInstance}`,
      tooltipsOpen: {},
      // Map of item.id → opt.id. We don't bind one v-model object to
      // the whole map because items can be added/removed; per-row
      // bindings keep things reactive without manual housekeeping.
      selections: {},
    };
  },
  computed: {
    chrome() {
      return CHROME[this.locale] || CHROME.en;
    },
    titleText() {
      return ((this.title && this.title[this.locale]) || '').trim();
    },
    subtitleText() {
      return ((this.subtitle && this.subtitle[this.locale]) || '').trim();
    },
    promptText() {
      return ((this.prompt && this.prompt[this.locale]) || '').trim();
    },
    displayTitle() {
      if (this.titleText) return this.titleText;
      return this.preview ? this.chrome.placeholderTitle : '';
    },
    displaySubtitle() {
      return this.subtitleText;
    },
    displayPrompt() {
      return this.promptText;
    },
    isTitlePlaceholder() {
      return !this.titleText;
    },
    displayOptions() {
      const fallback = this.chrome.placeholderShort;
      return (this.scaleOptions || []).map((opt, idx) => {
        const short = ((opt.label_short && opt.label_short[this.locale]) || '').trim();
        return {
          id: opt.id != null ? opt.id : `opt_${idx + 1}`,
          displayShort: short || (this.preview ? fallback : ''),
          isPlaceholder: !short,
        };
      });
    },
    displayItems() {
      return (this.items || []).map((item, idx) => {
        const label = ((item.label && item.label[this.locale]) || '').trim();
        const tip = ((item.tooltip_body && item.tooltip_body[this.locale]) || '').trim();
        return {
          id: item.id != null ? item.id : `item_${idx + 1}`,
          displayLabel: label
            || (this.preview ? `${this.chrome.placeholderItem} ${idx + 1}` : ''),
          isLabelPlaceholder: !label,
          hasTooltip: !!tip,
          tooltipText: tip,
        };
      });
    },
    answeredCount() {
      return Object.values(this.selections).filter(v => v != null).length;
    },
  },
  watch: {
    // When the active locale changes, an item that previously had a
    // tooltip in EN may have none in DE. Close any orphaned popovers
    // so the renderer doesn't show an empty body box.
    locale() {
      const valid = new Set(
        this.displayItems.filter(i => i.hasTooltip).map(i => i.id),
      );
      const next = {};
      for (const k of Object.keys(this.tooltipsOpen)) {
        if (valid.has(k) && this.tooltipsOpen[k]) next[k] = true;
      }
      this.tooltipsOpen = next;
    },
    // If an item is removed from the form, drop any selection or
    // tooltip state still associated with its id.
    items: {
      deep: false,
      handler(newItems) {
        const validIds = new Set((newItems || []).map(i => i.id));
        let changed = false;
        const sel = { ...this.selections };
        for (const id of Object.keys(sel)) {
          if (!validIds.has(id)) {
            delete sel[id];
            changed = true;
          }
        }
        if (changed) this.selections = sel;
      },
    },
  },
  methods: {
    toggleTooltip(itemId) {
      this.tooltipsOpen = {
        ...this.tooltipsOpen,
        [itemId]: !this.tooltipsOpen[itemId],
      };
    },
  },
};
</script>

<style scoped>
.matrix-renderer-wrap {
  --color-bg-page: #f5f4ef;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f1efe8;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.15);
  --color-progress-fill: #534AB7;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.5;
  padding: 1rem;
  display: flex;
  justify-content: center;
  box-sizing: border-box;
}

.matrix-renderer-wrap *,
.matrix-renderer-wrap *::before,
.matrix-renderer-wrap *::after {
  box-sizing: border-box;
}

.screen {
  width: 100%;
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1rem 1rem 0;
  display: flex;
  flex-direction: column;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.back-button {
  background: transparent;
  border: none;
  padding: 4px 8px;
  cursor: not-allowed;
  font-size: 18px;
  color: var(--color-text-secondary);
  line-height: 1;
  opacity: 0.3;
}

.section-counter {
  font-size: 12px;
  color: var(--color-text-secondary);
}

.progress-bar {
  display: flex;
  gap: 4px;
  margin-bottom: 1rem;
}

.progress-segment {
  flex: 1;
  height: 5px;
  border-radius: 3px;
  background: var(--color-bg-muted);
  overflow: hidden;
}

.progress-segment-fill {
  height: 100%;
  background: var(--color-progress-fill);
  border-radius: 3px;
  transition: width 0.25s ease;
}

.section-header {
  margin-bottom: 0.75rem;
}

.section-eyebrow {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-tertiary);
  margin: 0 0 4px;
}

.section-title {
  font-size: 18px;
  font-weight: 500;
  margin: 0 0 4px;
  line-height: 1.3;
}

.section-title.is-placeholder {
  color: var(--color-text-tertiary);
  font-style: italic;
}

.section-prompt {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 0 0 4px;
}

.section-progress-text {
  font-size: 12px;
  color: var(--color-text-tertiary);
  margin: 0;
}

.question-list {
  flex: 1;
  margin: 0 -1rem;
  padding: 0 1rem;
}

.question-row {
  padding: 0.75rem 0;
  border-bottom: 0.5px solid var(--color-border);
}

.question-row:last-child {
  border-bottom: none;
}

.question-row-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
}

.question-row-title {
  font-size: 13.5px;
  font-weight: 500;
  margin: 0;
  line-height: 1.4;
}

.question-row-title.is-placeholder {
  color: var(--color-text-tertiary);
  font-style: italic;
}

.tooltip-button-small {
  background: transparent;
  border: none;
  padding: 2px 6px;
  font-size: 13px;
  color: var(--color-text-tertiary);
  cursor: pointer;
  flex-shrink: 0;
  line-height: 1;
}

.tooltip-button-small:hover {
  color: var(--color-text-secondary);
}

.tooltip-content {
  background: var(--color-bg-muted);
  border-radius: var(--radius-md);
  padding: 8px 10px;
  font-size: 12px;
  color: var(--color-text-secondary);
  margin-bottom: 8px;
  line-height: 1.5;
}

.answer-chips {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.chip {
  flex: 1;
  min-width: 0;
  position: relative;
}

.chip input[type="radio"] {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.chip-label {
  display: block;
  text-align: center;
  padding: 8px 4px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: 11.5px;
  color: var(--color-text-secondary);
  cursor: pointer;
  background: var(--color-bg-card);
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  line-height: 1.3;
}

.chip.is-placeholder .chip-label {
  color: var(--color-text-tertiary);
  font-style: italic;
}

.chip-label:hover {
  background: var(--color-bg-muted);
}

.chip.is-selected .chip-label {
  border-color: var(--color-progress-fill);
  background: var(--color-bg-muted);
  color: var(--color-progress-fill);
  font-weight: 500;
}

.footer {
  padding: 0.75rem 1rem 1rem;
  background: var(--color-bg-card);
  border-top: 0.5px solid var(--color-border);
  margin: 0 -1rem;
  position: sticky;
  bottom: 0;
}

.next-button {
  width: 100%;
  padding: 11px;
  background: var(--color-bg-muted);
  color: var(--color-text-tertiary);
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: 14px;
  font-family: inherit;
  cursor: not-allowed;
}
</style>
